import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import type { Field, FieldValues } from '@/api/generated/model';
import { usePlayers, useTeams, useVenues } from '@/hooks/useApi';
import type { SportType } from '@/types';

export const initialFieldValues = (fields: Field[] = []): FieldValues => {
  const values: FieldValues = {};
  for (const field of fields) {
    const value = field.value ?? field.default;
    if (value !== undefined) values[field.key] = value;
  }
  return values;
};

export const isFieldVisible = (field: Field, values: FieldValues): boolean => {
  const rule = field.visibleWhen;
  if (!rule) return true;
  const current = values[rule.key];
  const asString = current === undefined || current === null ? '' : String(current);
  if (rule.eq !== undefined) return asString === rule.eq;
  if (rule.in) return rule.in.includes(asString);
  return Boolean(current);
};

const pad = (n: number) => String(n).padStart(2, '0');

const toLocalDateTime = (value: unknown): string => {
  if (typeof value !== 'string' || !value) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromLocalDateTime = (value: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

interface EntityOption {
  id: string;
  label: string;
}

interface EntityPickerProps {
  field: Field;
  value: unknown;
  error?: string;
  sportType?: SportType;
  onChange: (value: string[]) => void;
}

const EntityPicker: React.FC<EntityPickerProps> = ({ field, value, error, sportType, onChange }) => {
  const [query, setQuery] = useState('');
  const isPlayers = field.type === 'players';
  const players = usePlayers({ q: query || undefined, limit: 20 }, { queryKey: ['players', 'picker', query], enabled: isPlayers });
  const teams = useTeams(
    { q: query || undefined, limit: 20, sportType },
    { queryKey: ['teams', 'picker', query, sportType], enabled: !isPlayers },
  );
  const selectedIds = Array.isArray(value) ? (value as string[]) : [];
  const [known, setKnown] = useState<Record<string, string>>({});

  const options: EntityOption[] = useMemo(() => {
    if (field.options?.length) return field.options.map((o) => ({ id: o.value, label: o.label }));
    if (isPlayers) {
      return (players.data?.data ?? []).map((p) => ({ id: p.id, label: [p.firstName, p.lastName].filter(Boolean).join(' ') }));
    }
    return (teams.data?.data ?? []).map((t) => ({ id: t.id, label: t.name }));
  }, [field.options, isPlayers, players.data, teams.data]);

  const selected = selectedIds.map((id) => ({ id, label: known[id] ?? options.find((o) => o.id === id)?.label ?? id }));

  return (
    <Autocomplete
      multiple
      options={options}
      value={selected}
      loading={isPlayers ? players.isFetching : teams.isFetching}
      filterOptions={(x) => x}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.label}
      onInputChange={(_, v, reason) => reason === 'input' && setQuery(v)}
      onChange={(_, next) => {
        setKnown((prev) => ({ ...prev, ...Object.fromEntries(next.map((o) => [o.id, o.label])) }));
        onChange(next.map((o) => o.id));
      }}
      disabled={field.readOnly}
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.label}
          required={field.required}
          error={Boolean(error)}
          helperText={error ?? field.hint ?? 'Начните вводить имя'}
        />
      )}
    />
  );
};

const VenueSelect: React.FC<{ field: Field; value: unknown; error?: string; onChange: (v: string | null) => void }> = ({
  field,
  value,
  error,
  onChange,
}) => {
  const { data } = useVenues({ limit: 100 });
  const options = field.options?.length
    ? field.options.map((o) => ({ id: o.value, label: o.label }))
    : (data?.data ?? []).map((v) => ({ id: v.id, label: v.name }));
  return (
    <TextField
      select
      fullWidth
      label={field.label}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value || null)}
      required={field.required}
      disabled={field.readOnly}
      error={Boolean(error)}
      helperText={error ?? field.hint}
    >
      <MenuItem value="">Не указана</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

interface FieldInputProps {
  field: Field;
  value: unknown;
  error?: string;
  sportType?: SportType;
  onChange: (value: unknown) => void;
}

const FieldInput: React.FC<FieldInputProps> = ({ field, value, error, sportType, onChange }) => {
  const common = {
    fullWidth: true,
    label: field.label,
    required: field.required,
    disabled: field.readOnly,
    error: Boolean(error),
    helperText: error ?? field.hint,
  };

  switch (field.type) {
    case 'select':
      return (
        <TextField select {...common} value={value === undefined || value === null ? '' : String(value)} onChange={(e) => onChange(e.target.value)}>
          {!field.required && <MenuItem value="">—</MenuItem>}
          {(field.options ?? []).map((o) => (
            <MenuItem key={o.value} value={o.value}>
              <Box>
                <Typography variant="body1">{o.label}</Typography>
                {o.hint && (
                  <Typography variant="caption" color="text.secondary">
                    {o.hint}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </TextField>
      );
    case 'number':
    case 'duration':
      return (
        <TextField
          {...common}
          type="number"
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          slotProps={{
            htmlInput: { min: field.min, max: field.max, step: field.step, inputMode: 'numeric' },
            input: field.type === 'duration' ? { endAdornment: <InputAdornment position="end">мин</InputAdornment> } : undefined,
          }}
        />
      );
    case 'toggle':
      return (
        <Box>
          <FormControlLabel
            sx={{ minHeight: 48 }}
            control={<Switch checked={Boolean(value)} disabled={field.readOnly} onChange={(e) => onChange(e.target.checked)} />}
            label={field.label}
          />
          {(error || field.hint) && <FormHelperText error={Boolean(error)}>{error ?? field.hint}</FormHelperText>}
        </Box>
      );
    case 'date':
      return (
        <TextField
          {...common}
          type="date"
          value={typeof value === 'string' ? value.slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value || null)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      );
    case 'datetime':
      return (
        <TextField
          {...common}
          type="datetime-local"
          value={toLocalDateTime(value)}
          onChange={(e) => onChange(fromLocalDateTime(e.target.value))}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      );
    case 'venue':
      return <VenueSelect field={field} value={value} error={error} onChange={onChange} />;
    case 'players':
    case 'teams':
      return <EntityPicker field={field} value={value} error={error} sportType={sportType} onChange={onChange} />;
    case 'text':
    default:
      return (
        <TextField {...common} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
      );
  }
};

interface FieldsFormProps {
  fields: Field[];
  values: FieldValues;
  onChange: (values: FieldValues) => void;
  errors?: Record<string, string>;
  /** Контекст для выбора команд */
  sportType?: SportType;
}

/** Общий рендер описания полей с сервера (`Field[]`): основное + свёрнутое «Дополнительно». */
export const FieldsForm: React.FC<FieldsFormProps> = ({ fields, values, onChange, errors = {}, sportType }) => {
  const visible = fields.filter((f) => isFieldVisible(f, values));
  const main = visible.filter((f) => f.group !== 'advanced');
  const advanced = visible.filter((f) => f.group === 'advanced');
  const hasAdvancedError = advanced.some((f) => errors[f.key]);

  const render = (field: Field) => (
    <FieldInput
      key={field.key}
      field={field}
      value={values[field.key]}
      error={errors[field.key]}
      sportType={sportType}
      onChange={(value) => onChange({ ...values, [field.key]: value })}
    />
  );

  return (
    <Stack spacing={2}>
      {main.map(render)}
      {advanced.length > 0 && (
        <Accordion disableGutters defaultExpanded={hasAdvancedError} sx={{ bgcolor: 'inherit' }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48 }}>
            <Typography fontWeight={600}>Дополнительно</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>{advanced.map(render)}</Stack>
          </AccordionDetails>
        </Accordion>
      )}
      {errors._ && <FormHelperText error>{errors._}</FormHelperText>}
    </Stack>
  );
};
